import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Calendar, Info, Receipt, Lock } from 'lucide-react';
import { Holding, Transaction } from '../../types';
import { TaxPlan } from './TaxPlan';
import { RealisedDisposalRow, DisposalGroup } from './RealisedDisposalRow';
import { TaxExportButtons } from './TaxExportButtons';
import { ProFeatureLock } from '../Subscription/ProFeatureLock';
import { InfoTooltip } from '../ui/InfoTooltip';
import { loadCoinImages } from '../../utils/coinImageCache';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { useSubscription } from '../../hooks/useSubscription';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { FEATURE_GATES } from '../../config/limits';
import { formatAUD, formatAmount, formatDateShort } from '../../utils/formatters';
import {
  matchDisposalsWithDiagnostics,
  calculateFYCGT,
  auFinancialYearBounds,
  fyBoundsForStartYear,
  fyStartYearOfDate,
  roundAud,
  sumAud,
  TAX_BRACKETS,
  DEFAULT_BRACKET_IDX,
} from '../../utils/cgtCalculator';
import { warmRatesForDates } from '../../utils/fxRates';

/**
 * Top-level Tax tab.
 *  A. Financial-year banner (current AU FY)
 *  B. FY CGT summary — realized capital gains tax computed from this FY's sells
 *     (FIFO cost basis, 50% discount on 12mo+ gains, losses offset before discount)
 *  C+D. Tax Plan (saved estimates + Harvest Opportunities), reused unchanged
 *  E. Disclaimer
 *
 * Read-only / estimate: never affects holdings or P&L.
 */

const BRONZE = '#b89968';
const GREEN = '#6db981';
const RED = '#c4615e';
const BRACKET_KEY = 'cgtBracketIdx';

type SubTab = 'realised' | 'planned';
const SUBTABS: { id: SubTab; label: string }[] = [
  { id: 'realised', label: 'Realised CGT' },
  { id: 'planned',  label: 'Planned CGT' },
];

const statCardStyle: React.CSSProperties = {
  backgroundColor: 'rgb(var(--overlay) / 0.025)',
  border: '0.5px solid rgb(var(--overlay) / 0.06)',
  borderRadius: 10,
  padding: '14px 16px',
};

function readBracketIdx(): number {
  try {
    const raw = localStorage.getItem(BRACKET_KEY);
    if (raw == null) return DEFAULT_BRACKET_IDX;
    const n = parseInt(raw, 10);
    return n >= 0 && n < TAX_BRACKETS.length ? n : DEFAULT_BRACKET_IDX;
  } catch { return DEFAULT_BRACKET_IDX; }
}

function StatCard({ label, value, valueColor, sub }: { label: React.ReactNode; value: string; valueColor?: string; sub: React.ReactNode }) {
  return (
    <div style={statCardStyle}>
      <p className="uppercase mb-1.5" style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', color: 'var(--color-stat-label)' }}>
        {label}
      </p>
      <p className="leading-tight" style={{ fontSize: 19, fontWeight: 500, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', color: valueColor ?? 'rgb(var(--sl-100))' }}>
        {value}
      </p>
      <p className="text-[11px] mt-1.5" style={{ color: 'var(--color-sub)' }}>{sub}</p>
    </div>
  );
}

export function TaxView({ holdings, transactions }: { holdings: Holding[]; transactions: Transaction[] }) {
  const { rates } = useExchangeRates();
  const { canTax } = useSubscription();        // without the tax tier: locked preview
  const { open: openUpgradePrompt } = useUpgradePrompt();
  // Live AUD/USD. No longer used to convert the report — the engine converts each
  // leg at its own date's rate — but it is still the LAST-RESORT rate for a leg
  // whose date resolves to nothing at all.
  const spotAudPerUsd = rates['AUD'] ?? 1;

  // Sub-tab persists in the URL (?sub=…) so a refresh keeps the active sub-tab.
  // Changes use replace so they don't clutter back/forward (main tabs push).
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get('sub');
  const tab: SubTab = subParam === 'planned' ? 'planned' : 'realised';
  const setTab = (id: SubTab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('sub', id);
        return next;
      },
      { replace: true }
    );
  const [bracketIdx, setBracketIdx] = useState<number>(readBracketIdx);
  const [bracketAssumed, setBracketAssumed] = useState<boolean>(() => {
    try { return localStorage.getItem(BRACKET_KEY) == null; } catch { return true; }
  });
  const bracket = TAX_BRACKETS[bracketIdx];

  const changeBracket = (idx: number) => {
    setBracketIdx(idx);
    setBracketAssumed(false);
    try { localStorage.setItem(BRACKET_KEY, String(idx)); } catch { /* ignore */ }
  };

  const imageMap = useMemo(() => loadCoinImages(), []);

  // Selectable FYs = every AU FY with ≥1 transaction, plus the current FY. Newest first.
  const currentFyStartYear = useMemo(() => auFinancialYearBounds(new Date()).startYear, []);
  const fyStartYears = useMemo(() => {
    const years = new Set<number>([currentFyStartYear]);
    for (const tx of transactions) {
      const sy = fyStartYearOfDate(tx.date);
      if (isFinite(sy)) years.add(sy);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [transactions, currentFyStartYear]);

  // Selected FY (start year). Defaults to the current FY; falls back if it drops out.
  const [selectedFyStartYear, setSelectedFyStartYear] = useState<number>(currentFyStartYear);
  const activeFyStartYear = fyStartYears.includes(selectedFyStartYear) ? selectedFyStartYear : currentFyStartYear;

  const fy = useMemo(() => fyBoundsForStartYear(activeFyStartYear), [activeFyStartYear]);
  const fyLabel = `${fy.startYear}–${String(fy.endYear).slice(2)}`;
  const fyRange = `1 Jul ${fy.startYear} – 30 Jun ${fy.endYear}`;

  /**
   * The CGT engine resolves each leg's rate SYNCHRONOUSLY (it runs inside a
   * useMemo), so any date the bundled RBA table doesn't cover has to be fetched
   * before it runs — otherwise that leg silently degrades to the spot fallback.
   *
   * Warm first, then bump `fxVersion` to recompute with the now-complete view.
   * The bump is skipped when nothing was fetched, so the common case (every date
   * already in the bundle) costs no extra render.
   */
  const [fxVersion, setFxVersion] = useState(0);
  const txDatesKey = useMemo(
    () =>
      Array.from(
        new Set(transactions.filter((t) => t.type !== 'TRANSFER').map((t) => t.date))
      )
        .sort()
        .join(','),
    [transactions]
  );

  useEffect(() => {
    if (!txDatesKey) return;
    const controller = new AbortController();
    warmRatesForDates(txDatesKey.split(','), { signal: controller.signal })
      .then((fetched) => {
        if (fetched > 0 && !controller.signal.aborted) setFxVersion((v) => v + 1);
      })
      .catch(() => { /* offline — the engine falls back and flags the rows */ });
    return () => controller.abort();
  }, [txDatesKey]);

  // The diagnostics form, so a sell FIFO could not fully cover can be surfaced
  // rather than silently under-reported.
  const matched = useMemo(
    () => matchDisposalsWithDiagnostics(transactions, { spotAudPerUsd }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fxVersion is a
    // recompute trigger: the warmed rates live in a module cache, not in props.
    [transactions, spotAudPerUsd, fxVersion]
  );
  const result = useMemo(
    () => calculateFYCGT(matched.disposals, fy.startMs, fy.endMs, bracket.rate, matched.unmatched),
    [matched, fy.startMs, fy.endMs, bracket.rate]
  );

  // Unmatched disposals in OTHER financial years. The banner below is scoped to
  // the selected FY, so without this a year you are not currently looking at
  // reports nothing at all.
  const otherFyUnmatched = useMemo(() => {
    const years = new Set<number>();
    for (const u of matched.unmatched) {
      const sy = fyStartYearOfDate(u.date);
      if (isFinite(sy) && sy !== activeFyStartYear) years.add(sy);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [matched.unmatched, activeFyStartYear]);

  // Transactions exported alongside the CGT report are scoped to the selected FY.
  const fyTransactions = useMemo(
    () => transactions.filter((tx) => fyStartYearOfDate(tx.date) === activeFyStartYear),
    [transactions, activeFyStartYear]
  );

  // Group the FIFO sub-disposals into one row per SELL; the expanded view lists the
  // individual parcels. (Pure presentation grouping — no calculation change.)
  const groups = useMemo<DisposalGroup[]>(() => {
    const map = new Map<string, DisposalGroup>();
    for (const d of result.disposalsInFY) {
      let g = map.get(d.sellId);
      if (!g) {
        g = {
          sellId: d.sellId, coinId: d.coinId, symbol: d.symbol, name: d.name,
          disposalDate: d.disposalDate, disposalTs: d.disposalTs,
          totalQuantity: 0, totalProceeds: 0, totalCostBasis: 0, totalGain: 0,
          wallet: d.wallet, parcels: [], fxEstimated: false,
        };
        map.set(d.sellId, g);
      }
      g.totalQuantity  += d.quantity;
      if (!g.wallet && d.wallet) g.wallet = d.wallet;
      if (d.fxEstimated) g.fxEstimated = true;
      g.parcels.push(d);
    }
    const arr = Array.from(map.values());
    // Money totals are summed in whole cents from the engine's already-rounded
    // per-parcel figures, so a group total always equals the sum of the rows
    // shown under it — and of the rows in the CSV and the PDF.
    arr.forEach((g) => {
      g.totalProceeds  = sumAud(g.parcels.map((p) => p.proceeds));
      g.totalCostBasis = sumAud(g.parcels.map((p) => p.costBasis));
      g.totalGain      = sumAud(g.parcels.map((p) => p.gain));
    });
    arr.forEach((g) => g.parcels.sort(
      (a, b) => new Date(a.acquisitionDate).getTime() - new Date(b.acquisitionDate).getTime()
    ));
    return arr.sort((a, b) => b.disposalTs - a.disposalTs);
  }, [result.disposalsInFY]);

  const sellCount = groups.length;
  const grossNetAud = roundAud(result.totalGains - result.totalLosses);
  const isNetLoss = result.netLossCarriedForward > 1e-6;

  // The four headline figures, held as a value so the Pro view and the blurred
  // below-Pro preview render the SAME numbers from one definition.
  const statCards = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Reads as a chain: gross gain → minus discount → net (taxable) → tax owed. */}
      <StatCard
        label="Gross capital gain"
        value={isNetLoss ? formatAUD(0) : formatAUD(result.grossCapitalGain)}
        sub="after losses, before discount"
      />
      <StatCard
        label={
          <span className="inline-flex items-center gap-1.5">
            CGT discount applied
            <InfoTooltip text="Assets held over 12 months currently get a 50% discount on the capital gain. This applies to disposals up to 30 June 2027." />
          </span>
        }
        value={formatAUD(result.discountApplied)}
        sub="50% on eligible gains"
      />
      <StatCard
        label="Net capital gain"
        value={isNetLoss ? formatAUD(0) : formatAUD(result.netCapitalGain)}
        valueColor={!isNetLoss && result.netCapitalGain > 0 ? GREEN : undefined}
        sub={isNetLoss
          ? <>net loss <span style={{ color: RED }}>{formatAUD(result.netLossCarriedForward)}</span> carried forward</>
          : 'assessed for CGT'}
      />
      <StatCard
        label="Est. CGT owed"
        value={formatAUD(result.estCGT)}
        valueColor={BRONZE}
        sub={<>at {(bracket.rate * 100).toFixed(bracket.rate * 100 % 1 === 0 ? 0 : 1)}% marginal{bracketAssumed ? ' (assumed)' : ''}</>}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* A. Financial-year banner + FY switcher (applies to both sub-tabs) */}
      <div
        className="flex items-center gap-2.5 flex-wrap"
        style={{ background: 'rgba(212,180,131,0.06)', border: '0.5px solid rgba(212,180,131,0.2)', borderRadius: 10, padding: '9px 13px' }}
      >
        <Calendar className="w-4 h-4 flex-shrink-0" style={{ color: BRONZE }} />
        <p className="text-[12px]" style={{ color: 'var(--color-sub)' }}>
          Australian financial year <span style={{ color: 'rgb(var(--sl-100))' }}>{fyLabel}</span> ({fyRange}) · all figures in AUD
        </p>
        {canTax ? (
          <>
            <label className="sr-only" htmlFor="fy-select">Select financial year</label>
            <select
              id="fy-select"
              value={activeFyStartYear}
              onChange={(e) => setSelectedFyStartYear(parseInt(e.target.value, 10))}
              className="ml-auto bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-200 focus:outline-none focus:border-slate-500 cursor-pointer [color-scheme:dark]"
              title="View a different financial year"
            >
              {fyStartYears.map((sy) => (
                <option key={sy} value={sy}>
                  FY {sy}–{String(sy + 1).slice(2)}{sy === currentFyStartYear ? ' (current)' : ''}
                </option>
              ))}
            </select>
          </>
        ) : (
          // Free: the FY switcher is Pro-only. Show it (locked to the current FY) so
          // the feature is visible; clicking opens the upgrade prompt instead of switching.
          <button
            type="button"
            onClick={() => openUpgradePrompt(FEATURE_GATES.fySwitcher)}
            className="ml-auto flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-200 hover:border-slate-500 transition-colors"
            title="Viewing other financial years is a Pro feature"
          >
            FY {currentFyStartYear}–{String(currentFyStartYear + 1).slice(2)} (current)
            <Lock className="w-3 h-3" style={{ color: BRONZE }} />
          </button>
        )}
      </div>

      {/* Sub-tabs (underline style, matching the app's other tab rows) */}
      <div>
        <div className="flex gap-1 overflow-x-auto" style={{ borderBottom: '0.5px solid var(--tab-row-border)' }}>
          {SUBTABS.map((t) => {
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className="whitespace-nowrap transition-colors"
                style={{
                  background: 'transparent',
                  padding: '10px 14px',
                  fontSize: '13px',
                  borderBottom: isActive ? '2px solid var(--tab-accent)' : '2px solid transparent',
                  marginBottom: '-0.5px',
                  color: isActive ? 'var(--tab-text-active)' : 'var(--tab-text-inactive)',
                  fontWeight: isActive ? 500 : 400,
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-hover)'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = 'var(--tab-text-inactive)'; }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Panels — kept mounted, toggled via display to preserve state */}
        <div className="pt-6">

          {/* TAB 1 — Realised CGT */}
          <div style={{ display: tab === 'realised' ? 'block' : 'none' }}>
      <section>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <p className="uppercase mb-1.5" style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', color: 'var(--color-bronze)' }}>
              {activeFyStartYear === currentFyStartYear ? 'This financial year' : `Financial year ${fyLabel}`}
            </p>
            <h2 className="text-slate-100" style={{ fontSize: 19, fontWeight: 500 }}>Estimated capital gains tax</h2>
            <p className="text-slate-400 mt-1.5" style={{ fontSize: 13 }}>Based on your actual sell transactions this FY.</p>
          </div>
          {/* Bracket selector */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <label className="block text-[10px] uppercase tracking-wider" style={{ color: BRONZE }}>Your income bracket</label>
              <InfoTooltip text="Your highest marginal rate, including Medicare levy where relevant. Used to estimate tax owed. Change it to model a different income year." />
            </div>
            {canTax ? (
              <select
                value={bracketIdx}
                onChange={(e) => changeBracket(parseInt(e.target.value, 10))}
                className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-slate-500 cursor-pointer [color-scheme:dark]"
              >
                {TAX_BRACKETS.map((b, i) => (
                  <option key={i} value={i}>{b.label} - {(b.rate * 100).toFixed(b.rate * 100 % 1 === 0 ? 0 : 1)}%</option>
                ))}
              </select>
            ) : (
              // Below Pro the only figures this drives are blurred, so a working
              // selector would change nothing on screen. Same treatment as the FY
              // switcher above: visible, inert, opens the upgrade prompt.
              <button
                type="button"
                onClick={() => openUpgradePrompt(FEATURE_GATES.cgtBreakdown)}
                className="flex items-center gap-1.5 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 hover:border-slate-500 transition-colors"
                title="Estimating tax at your marginal rate is a Pro feature"
              >
                {bracket.label} - {(bracket.rate * 100).toFixed(bracket.rate * 100 % 1 === 0 ? 0 : 1)}%
                <Lock className="w-3 h-3" style={{ color: BRONZE }} />
              </button>
            )}
          </div>
        </div>

        {/* Below Pro the whole realised view is ONE blurred preview with ONE CTA.
            Real figures, unreadable: cheaper to keep honest than a mocked mirror,
            and more persuasive, because they are the user's own numbers. */}
        {canTax ? (
          <>
            <div className="mt-5">{statCards}</div>

            {/* CSV / PDF exports — Pro only (Free → clicking opens the UpgradePrompt) */}
            <TaxExportButtons
              result={result}
              fy={{ startYear: fy.startYear, endYear: fy.endYear }}
              marginalRate={bracket.rate}
              transactions={fyTransactions}
            />

            {/* Disposals breakdown */}
            <div className="mt-6">
              {/* A sell FIFO could not fully cover. Prominent rather than a footnote,
                  because it means the gain BELOW is understated — the unmatched coins
                  were disposed of but carry no cost basis, so they produce no row.
                  Almost always a missing or mis-dated BUY. */}
              {result.hasUnmatched && (
                <div
                  className="flex items-start gap-2.5 p-3.5 rounded-xl mb-3"
                  style={{ background: 'rgba(196,97,94,0.07)', border: '0.5px solid rgba(196,97,94,0.3)' }}
                >
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: RED }} />
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium" style={{ color: 'rgb(var(--sl-100))' }}>
                      {result.unmatchedInFY.length === 1
                        ? 'A disposal could not be fully matched'
                        : `${result.unmatchedInFY.length} disposals could not be fully matched`}
                    </p>
                    <p className="text-[11px] mt-1 leading-relaxed" style={{ color: 'var(--color-sub)' }}>
                      You sold more than your recorded purchases cover, so part of each sale below has no
                      cost basis and is left out of the figures above. The gain shown is therefore
                      understated. Check for a missing buy, or one dated after the sale.
                      {otherFyUnmatched.length > 0 && (
                        <>
                          {' '}
                          <span style={{ color: BRONZE }}>
                            Also affects {otherFyUnmatched.map((sy) => `FY${sy}-${String(sy + 1).slice(2)}`).join(', ')}.
                          </span>
                        </>
                      )}
                    </p>
                    <ul className="mt-2 space-y-0.5">
                      {result.unmatchedInFY.map((u) => (
                        <li key={u.sellId} className="text-[11px] num" style={{ color: BRONZE }}>
                          {formatDateShort(u.date)} · {u.name}: {formatAmount(u.quantity)} {u.symbol} unmatched
                          {u.matchedQuantity > 0 && (
                            <span style={{ color: 'var(--color-sub)' }}>
                              {' '}(of {formatAmount(u.matchedQuantity + u.quantity)} sold)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <p className="text-[13px] font-medium" style={{ color: 'rgb(var(--sl-100))' }}>Realised disposals this FY</p>
                  <InfoTooltip text="Realised gains come from assets you've actually sold and are taxable. Unrealised gains are on paper only and aren't taxed until you sell." />
                </div>
                {groups.length > 0 && (
                  <p className="text-[12px]" style={{ color: 'var(--color-sub)' }}>
                    {sellCount} sell{sellCount === 1 ? '' : 's'} · net{' '}
                    <span className="num font-semibold" style={{ color: grossNetAud >= 0 ? GREEN : RED }}>
                      {grossNetAud >= 0 ? '+' : '−'}{formatAUD(Math.abs(grossNetAud))}
                    </span>
                  </p>
                )}
              </div>

              {groups.length === 0 ? (
                <div className="flex items-center gap-3 p-5 rounded-xl" style={{ background: 'rgb(var(--overlay) / 0.02)', border: '0.5px solid rgb(var(--overlay) / 0.06)' }}>
                  <Receipt className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--color-sub)' }} />
                  <p className="text-[13px]" style={{ color: 'var(--color-sub)' }}>
                    No disposals this financial year yet. CGT is calculated from your sell transactions.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {groups.map((g) => (
                    <RealisedDisposalRow
                      key={g.sellId}
                      group={g}
                      marginalRate={bracket.rate}
                      bracketLabel={bracket.label}
                      imageMap={imageMap}
                    />
                  ))}
                </div>
              )}

              {/* Method note */}
              <p className="text-[11px] italic mt-3" style={{ color: 'var(--color-sub)' }}>
                Cost basis calculated using the FIFO method (first in, first out).{' '}
                <InfoTooltip text="First in, first out. When you sell part of a holding, the oldest parcel is treated as sold first. MyCryptoPlanner uses FIFO for all estimates." />{' '}
                Losses are applied against gains before the 50% discount. Your actual CGT depends on your chosen accounting method and circumstances.{' '}
                AUD amounts use the exchange rate for each transaction's own date (RBA daily rates, falling back to ECB), rounded to the nearest cent per transaction.
              </p>

              {/* Only shown when it is true: a report that silently used today's rate
                  for a past date would look exactly like one that did not. */}
              {result.fxEstimated && (
                <p className="text-[11px] mt-2" style={{ color: BRONZE }}>
                  Some disposals had no published exchange rate for their date and use the most recent earlier rate instead (or today's, if there was none), so those figures are approximate. Expand a row to see which.
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="mt-5">
            <ProFeatureLock
              message="Australian CGT, worked out from your own sales. See your gross capital gain, the 50% discount, your net gain and the estimated tax owed, with every disposal broken down and ready to export for your accountant. Available on Pro."
              gate={FEATURE_GATES.cgtBreakdown}
              minHeight={340}
            >
              <div className="p-4">
                {statCards}
                <p className="text-[13px] font-medium mt-5 mb-3" style={{ color: 'rgb(var(--sl-100))' }}>
                  Realised disposals this FY
                </p>
                {groups.length > 0 && (
                  <div className="space-y-2">
                    {groups.slice(0, 3).map((g) => (
                      <RealisedDisposalRow
                        key={g.sellId}
                        group={g}
                        marginalRate={bracket.rate}
                        bracketLabel={bracket.label}
                        imageMap={imageMap}
                      />
                    ))}
                  </div>
                )}
              </div>
            </ProFeatureLock>
          </div>
        )}
      </section>
          </div>

          {/* TAB 2 — Planned Sales (saved estimates + Harvest Opportunities) — Pro only */}
          <div style={{ display: tab === 'planned' ? 'block' : 'none' }}>
            {canTax ? (
              <TaxPlan holdings={holdings} />
            ) : (
              <ProFeatureLock
                message="The tax planner, including saved estimates and harvest opportunities, is a Pro feature. Upgrade to Pro to plan future sales."
                gate={FEATURE_GATES.plannedSales}
                minHeight={260}
              />
            )}
          </div>

        </div>
      </div>

      {/* Disclaimer (shown below both sub-tabs) */}
      <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-slate-900/40 border border-slate-700/50">
        <Info className="w-3.5 h-3.5 text-slate-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500 leading-relaxed">
          This is an estimate only and does not constitute financial or tax advice. Calculations use
          simplified ATO CGT rules and may not reflect your circumstances. This tab covers capital gains
          only. It does not include dividend income, franking credits, or any other income type, so
          holders of Australian shares in particular will have dividends and franking amounts that are not
          reflected here. Consult a registered tax agent.
        </p>
      </div>
    </div>
  );
}
